import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useCurrentFrame } from '../use-current-frame';
import { useVideoConfig } from '../use-video-config';
import { interpolate } from '../interpolate';

const DustContainer = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 4;
`;

const Particle = styled.span`
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 215, 100, 0.5);
`;

const PARTICLE_COUNT = 14;

/**
 * Deterministic seeded random number generator.
 * Produces consistent values across renders for same seed.
 */
function seededRandom(seed) {
  let s = seed;
  return () => {
    s = (s * 1664525 + 1013904223) & 0xffffffff;
    return (s >>> 0) / 0xffffffff;
  };
}

/**
 * DustParticles — renders floating dust/sparkle particles
 * driven entirely by useCurrentFrame() for Puppeteer compatibility.
 *
 * Each particle floats upward in a cycle, fading in and out.
 * All positions and timing are deterministic from a seed.
 *
 * Props:
 *   - seed: number (default 42) — for reproducible random positions
 *   - count: number (default 14)
 */
export function DustParticles({ seed = 42, count = PARTICLE_COUNT }) {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Generate particle configs once (deterministic from seed)
  const particles = useMemo(() => {
    const rng = seededRandom(seed);
    return Array.from({ length: count }, (_, i) => ({
      id: i,
      x: rng() * 100, // left %
      y: rng() * 100, // initial top %
      size: 2 + rng() * 3, // px
      cycleDuration: (4 + rng() * 4) * fps, // frames for one full cycle
      offset: rng() * 6 * fps, // frame offset (stagger start)
    }));
  }, [seed, count, fps]);

  return (
    <DustContainer>
      {particles.map((p) => {
        // Compute looped frame position for this particle
        const cycleFrame = ((frame + p.offset) % p.cycleDuration);
        const progress = cycleFrame / p.cycleDuration; // 0..1

        // Y translation: float upward by 80px over the cycle
        const translateY = interpolate(
          progress,
          [0, 1],
          [0, -80],
          { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
        );

        // Opacity: fade in at 10%, fade out at 90%
        const opacity = interpolate(
          progress,
          [0, 0.1, 0.9, 1],
          [0, 0.7, 0.3, 0],
          { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
        );

        return (
          <Particle
            key={p.id}
            style={{
              left: `${p.x}%`,
              top: `${p.y}%`,
              width: `${p.size}px`,
              height: `${p.size}px`,
              opacity,
              transform: `translateY(${translateY}px)`,
            }}
          />
        );
      })}
    </DustContainer>
  );
}
